import type { PushSuppressedResponse } from '../../../api/client.ts';

/** Known `suppressed` reasons of the pipeline and push reachability (BB-49, BB-72, BB-78, BB-98, BB-102). */
const REASON_LABELS: Record<string, string> = {
  no_reachable_device: 'No reachable device (no token, notifications off or app out of scope)',
  no_push_token: 'No push token',
  os_permission_denied: 'Notifications turned off on the device',
  app_not_in_scope: 'App not targeted',
  unsubscribed: 'Unsubscribed',
  stale_event: 'Trigger event too old',
  dedupe_window: 'Duplicate within the dedupe window',
  quiet_hours: 'Quiet hours',
  frequency_cap: 'Frequency capping rule',
  rate_limit: 'Rate limit',
  global_control: 'Global control group',
  suppression_list: 'Suppression list',
  not_reeligible: 'Not re-eligible yet',
};
const STAGE_LABELS: Record<string, string> = { provider: 'Rejected by the provider', pre_send: 'Failed before sending' };

export const suppressedReasonLabel = (reason: string) => REASON_LABELS[reason] ?? reason;

interface Bar {
  key: string;
  label: string;
  detail?: string;
  count: number;
}

function Bars({ title, bars, max }: { title: string; bars: Bar[]; max: number }) {
  return (
    <table className="table bar-table" aria-label={title}>
      <caption>{title}</caption>
      <thead>
        <tr>
          <th scope="col">{title === 'Not sent' ? 'Reason' : 'Code'}</th>
          <th scope="col">Pushes</th>
          <th scope="col">
            <span className="visually-hidden">Share of the largest</span>
          </th>
        </tr>
      </thead>
      <tbody>
        {bars.map((b) => (
          <tr key={b.key}>
            <th scope="row">
              {b.label}
              {b.detail && <span className="hint"> · {b.detail}</span>}
            </th>
            <td className="bar-table__value">{b.count.toLocaleString('en-US')}</td>
            <td className="bar-table__bar" aria-hidden="true">
              <span className="bar-table__fill" style={{ width: `${Math.max(2, (b.count / max) * 100)}%` }} title={`${b.label}: ${b.count.toLocaleString('en-US')}`} />
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/**
 * Why pushes were not sent or failed (BB-79, for BB-138): `suppressed` reasons and `failed` codes by stage, as bars
 * with their counts. The chart is a table, so the numbers read without the bars (screen readers, print).
 */
export function SuppressedReasonsChart({ data }: { data: PushSuppressedResponse }) {
  const suppressed: Bar[] = data.suppressed.map((s) => ({ key: s.reason, label: suppressedReasonLabel(s.reason), ...(REASON_LABELS[s.reason] ? { detail: s.reason } : {}), count: s.count }));
  const failed: Bar[] = data.failed.map((f) => ({ key: `${f.stage}:${f.code}`, label: f.code, detail: STAGE_LABELS[f.stage] ?? f.stage, count: f.count }));
  if (!suppressed.length && !failed.length) return <p className="hint">Every push in the period went out: nothing was suppressed or failed.</p>;
  const max = Math.max(1, ...suppressed.map((b) => b.count), ...failed.map((b) => b.count));
  return (
    <div className="suppressed-chart">
      {suppressed.length > 0 && <Bars title="Not sent" bars={suppressed} max={max} />}
      {failed.length > 0 && <Bars title="Failed" bars={failed} max={max} />}
    </div>
  );
}
